import { auth, db } from "../firebase";
import { doc, setDoc, writeBatch, updateDoc, serverTimestamp } from "firebase/firestore";
import { generateTripSlug } from "./slugify";
import { stripUndefined } from "./helpers";
import { countryToFlag } from "./countryFlags";
import { searchPhoto } from "./pexels";
import { generateTripActivities } from "./generateTripActivities";

/**
 * Create a new trip document in Firestore.
 *
 * @param {Object} brief - TripBrief object
 * @param {string} brief.title
 * @param {string[]} brief.travelers
 * @param {string} brief.style
 * @param {string} brief.budget
 * @param {string} brief.departureCity
 * @param {string} brief.departureDate
 * @param {string} brief.returnDate
 * @param {string[]} brief.destinations
 * @param {string[]} brief.mustSees
 * @param {string[]} brief.avoid
 * @returns {Promise<string>} The unique slug (also the doc ID)
 */
export async function createTripDocument(brief) {
  if (!brief?.title?.trim()) {
    throw new Error("Trip title is required");
  }
  if (!brief.departureDate || !/^\d{4}-\d{2}-\d{2}$/.test(brief.departureDate)) {
    throw new Error("Departure date is required (YYYY-MM-DD)");
  }

  try {
    const slug = generateTripSlug(brief.title, brief.departureDate);

    await setDoc(doc(db, "trips", slug), {
      id: slug,
      slug: slug,
      title: brief.title,
      ownerUid: auth.currentUser.uid,
      status: "creating",
      brief: { ...brief },
      createdAt: serverTimestamp(),
    });

    return slug;
  } catch (err) {
    throw new Error(`Failed to create trip: ${err.message}`);
  }
}

/**
 * Write day docs to Firestore, collect cities, fetch hero image,
 * and optionally set wave3Started + fire generateTripActivities.
 *
 * @param {Object} opts
 * @param {string} opts.slug - Trip document ID
 * @param {Object} opts.skeleton - { days: [...], tripTitle: "..." }
 * @param {Object} opts.brief - Trip brief object
 * @param {string} opts.apiUrl - Proxy server URL
 * @param {Array|null} opts.wave2Days - Must-see days (null for fast path)
 * @param {boolean} opts.skipWizard - true = fast path (fire generation), false = wizard path (stop at wave1_complete)
 * @returns {Promise<{ cities: string[] }>}
 */
export async function writeDaysAndStartGeneration({ slug, skeleton, brief, apiUrl, wave2Days, skipWizard = false }) {
  // 1. Write day docs + update trip doc in a single batch
  const batch = writeBatch(db);

  skeleton.days.forEach(d => {
    const dayId = `${slug}-day-${d.dayNumber}`;
    const dayDoc = stripUndefined({
      tripId: slug,
      ownerUid: auth.currentUser.uid,
      order: d.dayNumber,
      city: d.city,
      country: d.country,
      flag: countryToFlag(d.country),
      theme: d.isTransitDay ? 'Travel Day' : `Day in ${d.city}`,
      tip: "",
      heroImg: null,
      hidden: false,
      locked: false,
      isTransitDay: d.isTransitDay || false,
      transitMode: d.transitMode || null,
      routingRationale: d.routingRationale,
      climate: d.climate || null,
    });
    batch.set(doc(db, "days", dayId), dayDoc, { merge: true });
  });

  batch.update(doc(db, "trips", slug), {
    status: "wave1_complete",
    title: skeleton.tripTitle,
  });

  await batch.commit();

  // 2. Collect unique cities in display order
  const seen = new Set();
  const cities = [];
  skeleton.days.forEach(d => {
    if (d.city && !seen.has(d.city)) {
      seen.add(d.city);
      cities.push(d.city);
    }
  });

  const tripRef = doc(db, "trips", slug);
  try {
    await updateDoc(tripRef, { cities });
  } catch (err) {
    console.error("[writeDaysAndStartGeneration] Cities write failed:", err);
  }

  // 3. Fire-and-forget Pexels hero image fetch — use first destination, not departure city
  const departureNorm = (brief.departureCity || '').trim().toLowerCase();
  const firstDestination = cities.find(c => c.trim().toLowerCase() !== departureNorm);
  const heroQuery = firstDestination || (brief.destinations && brief.destinations[0]) || skeleton.tripTitle || "";
  if (heroQuery) {
    searchPhoto(heroQuery + " travel landscape")
      .then(url => updateDoc(tripRef, { heroImg: url || null }))
      .catch(err => console.error("[writeDaysAndStartGeneration] Hero image fetch failed:", err));
  }

  // 4–5. Only fire generation on the fast path (skipWizard: true)
  if (skipWizard) {
    try {
      await updateDoc(tripRef, { status: "creating", wave3Started: true, generationStartedAt: serverTimestamp() });
    } catch (err) {
      console.error("[writeDaysAndStartGeneration] Wave3 status update failed:", err);
    }

    const daysForWave3 = skeleton.days.map(d => ({
      dayNumber: d.dayNumber,
      city: d.city,
      country: d.country,
      isTransitDay: d.isTransitDay || false,
      transitMode: d.transitMode || null,
    }));

    try {
      generateTripActivities(
        slug,
        daysForWave3,
        {},
        apiUrl,
        brief || {},
        wave2Days || [],
      );
    } catch (err) {
      console.error("[writeDaysAndStartGeneration] Wave3 generation failed:", err);
    }
  }

  // 6. Return cities for caller use
  return { cities };
}
